import { useCloned } from '@vueuse/core';
import {
  CardFormInput,
  ConversionFormInput,
  GroupForDateOperationHistory,
  InterBankFormInput,
  InterBankTransferOperation,
  IntraBankFormInput,
  ListenPayment,
  ListenPaymentHistory,
  OperationHistory,
  OperationTypes,
  PaymentBudget,
  PaymentByCriteriaParams,
  PaymentCardTransfer,
  PaymentConversion,
  PaymentForm,
  PaymentQrTransfer,
  PaymentStatusHistoryInfo,
  PaymentSwift,
  PaymentTransfer,
  PaymentTransferByInvoice,
  QrPaymentForm,
  SwiftFormInput,
  TransferForm,
  PaymentRko2CardTransfer,
  PaymentQrReturned,
  SalaryStatement,
  PaginatedSalaryStatement,
  SalaryInfoWithDetail,
} from './types';
import {
  BankFragment,
  BudgetPaymentRecord,
  CardTransferInput,
  ConversionInput,
  ConversionPaymentRecordFragment,
  CurrencyForPaymentFragment,
  CurrencyRateFragment,
  CurrencyRatesFragment,
  OperationFragment,
  OperationHistoryByCriteriaFragment,
  Operations,
  PaginatedBank,
  PaginatedBudgetClassificationCode,
  PaginatedOperationTypeCategory,
  PaymentStatusHistoryFragment,
  QrTransferRecordFragment,
  SearchOperationHistoryInput,
  Status,
  SwiftInput,
  SwiftPaymentRecordFragment,
  TemplateInput,
  TransferInput,
  TransferPaymentRecordFragment,
  TransferTemplateInput,
  CardTransferRecordFragment,
  RefundedQrOperationRecordFragment,
  QrOperationFilterInput,
  QrTransferFilterInput,
  QrTransferInput,
  SalaryInfoWithDetailFragment,
  PaginatedSalaryFragment,
  SalaryRecordFragment,
  CountryFragment,
  QrOperationInstallmentFilterInput,
  QrOperationType,
  QrOperationStatus,
  QrOperationInfoInstallmentFragment,
  PersonalQrOperationFilterInput,
  Origin,
} from '@/app/graphql/payment/sdk.payment';
import type { TransferByInvoiceDetailFragment } from '@/app/dev-utils/payment.sdk';
import { formatDateIso, formatDateView } from '@/shared/lib/date';
import {
  generatePaymentStatusLabel,
  generatePaymentType,
  generateSalaryStatementStatusLabel,
  getPaymentImg,
  mapDateTime,
  ParamsPayment,
  ParamsPaymentSubscription,
} from '@/shared/web/helpers';
import { date } from 'quasar';
import { User } from '@/app/stores/user/types';
import {
  PayControlKeyAvailabilityFragment,
  SignRight,
  SignScheme,
  SignType,
} from '@/app/graphql/access-control/sdk.access-control';
import { formatCurrency } from '@/shared/lib/format-currency';
import dayjs from 'dayjs';
import { useAuthUserStore } from '@/app/stores/user';
import { RuleTrait, TimeRuleDto } from '@/app/graphql/rule-engine/sdk.rule-engine';
import { Currencies } from '@/shared/services/account';
import formatDate = date.formatDate;
import { SalaryContent } from '@/shared/services/import-payment';
import { InstallmentFilter, InstallmentOperation, InstallmentOperations } from '@/shared/services/installment';

export class GraphQlError extends Error {
  graphQLErrors: {
    message: string;
    extensions: { classification?: string };
  }[] = [];
  constructor(message: string, code?: string) {
    super();
    this.graphQLErrors = [
      {
        message,
        extensions: { classification: code },
      },
    ];
  }
}

export function checkSystemError(id: number) {
  if (id === -1) throw new GraphQlError('Ошибка системы');

  return { paymentId: id };
}

export function mapPaymentHistory(payments: OperationHistoryByCriteriaFragment[], user: User): OperationHistory[] {
  return payments.map((payment) => mapPaymentNotActions(payment, user));
}

function sortedPaymentDates(payments: GroupForDateOperationHistory) {
  const sorted = Object.entries(payments).sort((a, b) => {
    const dateA = dayjs(a[0]).unix();
    const dateB = dayjs(b[0]).unix();
    if (dateB > dateA) return 1;
    if (dateB < dateA) return -1;
    return 0;
  });
  return Object.fromEntries(sorted);
}

export function groupPaymentForDate(newPayments: OperationHistory[], oldPayments: GroupForDateOperationHistory) {
  const payments = useCloned(oldPayments).cloned.value;
  for (let i = 0; i < newPayments.length; i++) {
    const item = newPayments[i];
    const date = item.createdDate.substring(0, 10);
    if (!payments[date]) {
      payments[date] = [];
    } else {
      const hasPayment = payments[date].some((operation) => operation.id === item.id);
      if (hasPayment) continue;
    }
    payments[date].push(item);
    payments[date].sort((a, b) => dayjs(b.createdDate).unix() - dayjs(a.createdDate).unix());
  }
  return sortedPaymentDates(payments);
}

export function mapPaymentTransferData(
  payment: TransferPaymentRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentTransfer {
  const result = mapPayment(payment, user, timeRule, keyAvailability);
  return {
    ...result,
  };
}

export function mapPaymentTransferByInvoiceData(
  payment: TransferByInvoiceDetailFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentTransferByInvoice {
  const normalizedPayment: TransferPaymentRecordFragment = {
    __typename: 'TransferPaymentRecord',
    id: payment.paymentId,
    createdDate: payment.createdDate,
    createdBy: payment.createdBy,
    updatedBy: payment.creatorName,
    legalPartyId: payment.legalPartyId,
    operationCode: Operations.TransferByInvoice,
    description: null,
    amount: payment.amount,
    currencyId: 0,
    currencyCode: payment.isoCode,
    payerAccount: payment.payerAccount,
    payerBic: '',
    payerBicName: '',
    payerInn: '',
    payerName: '',
    paymentCode: null,
    purpose: payment.purpose,
    recipientAccount: payment.recipientAccount,
    recipientBic: '',
    recipientBicName: '',
    recipientInn: null,
    recipientName: payment.recipientName,
    transferNum: payment.transferNum,
    valueDate: payment.valueDate,
    statusCode: payment.statusCode,
    creatorName: payment.creatorName,
    origin: Origin.Manual,
  };

  const mapped = mapPaymentTransferData(normalizedPayment, user, timeRule, keyAvailability);
  const paramsPayment: ParamsPayment = {
    statusCode: payment.statusCode,
    createdBy: payment.createdBy,
    updatedBy: payment.creatorName,
    valueDate: payment.valueDate,
    operationCode: Operations.TransferByInvoice,
    userReferenceId: user.userReferenceId,
    signRight: user.currentContract?.userContractAttributes?.signRight,
    signScheme: user.currentContract?.signScheme,
    signType: user.currentContract?.signType,
    timeRule:
      timeRule.find((item) => item.ruleTrait === payment.isoCode) ||
      timeRule.find((item) => item.ruleTrait === RuleTrait.Default)!,
    keyAvailability,
  };

  return {
    ...mapped,
    invoiceId: payment.invoiceId,
    isRevokable: checkPaymentRevokable(payment.statusCode, paramsPayment),
    isRemovable: checkPaymentRemovable(payment.statusCode, paramsPayment),
    isUpdateble: checkPaymentUpdateble(payment.statusCode, paramsPayment),
    isRecall: checkPaymentRecall(payment.statusCode, paramsPayment),
    isRepeatable: false,
  };
}

export function mapPaymentRko2CardTransferData(
  payment: CardTransferRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentCardTransfer {
  const result = mapPayment(payment, user, timeRule, keyAvailability);
  return {
    ...result,
    recipientAccount: payment.recipientAccount,
    recipientCardNum: payment.recipientCardNum,
  };
}

export function mapPaymentConversionData(
  payment: ConversionPaymentRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentConversion {
  const result = mapPayment(payment, user, timeRule, keyAvailability);
  return {
    ...result,
    formattedRecipientAmount: formatCurrency(payment.recipientAmount),
  };
}

export function mapPaymentSwiftData(
  payment: SwiftPaymentRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentSwift {
  const result = mapPayment(payment, user, timeRule, keyAvailability);
  return {
    ...result,
  };
}

export function mapPaymentBudgetData(
  payment: BudgetPaymentRecord,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentSwift {
  const result = mapPayment(payment, user, timeRule, keyAvailability);
  return {
    ...result,
  };
}

export function mapPaymentQrRefundedData(
  payment: RefundedQrOperationRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentQrReturned {
  const ruleTrait = (payment.cardTransferRko2CardRecord as CardTransferRecordFragment).currencyCode;

  const paramsPayment: ParamsPayment = {
    statusCode: payment.cardTransferRko2CardRecord.statusCode,
    createdBy: payment.cardTransferRko2CardRecord.creatorName,
    //TODO: Убрать, когда на бэке добавят поле
    //@ts-ignore
    updatedBy: payment.cardTransferRko2CardRecord.updatedBy,
    valueDate: payment.cardTransferRko2CardRecord.creatorName,
    operationCode: payment.cardTransferRko2CardRecord.operationCode,
    userReferenceId: user.userReferenceId,
    signRight: user.currentContract?.userContractAttributes?.signRight,
    signScheme: user.currentContract?.signScheme,
    signType: user.currentContract?.signType,
    timeRule:
      timeRule.find((item) => item.ruleTrait === ruleTrait) ||
      timeRule.find((item) => item.ruleTrait === RuleTrait.Default)!,
    keyAvailability,
  };

  return {
    ...payment,
    id: payment.cardTransferRko2CardRecord.id,
    legalPartyId: user.currentContract?.orgReferenceId || 0,
    createdDate: payment.cardTransferRko2CardRecord.createdDate,
    amount: payment.cardTransferRko2CardRecord.amount,
    operationCode: Operations.Refund,
    valueDate: payment.cardTransferRko2CardRecord.createdDate,
    createdBy: '',
    payerName: '',
    origin: '',
    purpose: '',
    payerAccount: '',
    recipientAccount: '',
    recipientDetail: payment.cardTransferRko2CardRecord.recipientDetail,
    currencyCode: 'KGS',
    statusCode: payment.cardTransferRko2CardRecord.statusCode,
    transferNum: '',
    formattedAmount: formatCurrency(payment.cardTransferRko2CardRecord.amount),
    paymentStatus: generatePaymentStatusLabel(paramsPayment) || { label: '', classList: '' },
    paymentType: generatePaymentType(Operations.Refund) || '',
    paymentDate: mapDateTime(payment.cardTransferRko2CardRecord.createdDate),
    paymentImg: getPaymentImg(Operations.Refund),
    paymentValueDate: formatDate(Operations.Refund, 'DD.MM.YYYY'),
    selected: false,
    isRepeatable: false,
    isEditable: false,
    isSignable: checkPaymentSignable(payment.cardTransferRko2CardRecord.statusCode, paramsPayment),
    isExportable: true,
    isOpenPdf: true,
    isRevokable: false,
    isRemovable: false,
    isSendForSign: false,
    isUpdateble: false,
    isRecall: false,
  };
}

export function mapPaymentQrData(
  payment: QrTransferRecordFragment,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): PaymentQrTransfer {
  const paramsPayment: ParamsPayment = {
    statusCode: payment.statusCode,
    createdBy: payment.creatorName,
    valueDate: payment.createdDateTime,
    //@ts-ignore
    updatedBy: payment.updatedBy,
    operationCode: Operations.Qr,
    userReferenceId: user.userReferenceId,
    signRight: user.currentContract?.userContractAttributes?.signRight,
    signScheme: user.currentContract?.signScheme,
    signType: user.currentContract?.signType,
    timeRule:
      timeRule.find((item) => item.ruleTrait === payment.currencyCode) ||
      timeRule.find((item) => item.ruleTrait === RuleTrait.Default)!,
    keyAvailability,
  };

  return {
    ...payment,
    id: payment.paymentId,
    legalPartyId: user.currentContract?.orgReferenceId || 0,
    createdDate: payment.createdDateTime,
    amount: payment.amount,
    operationCode: Operations.Qr,
    valueDate: payment.createdDateTime,
    purpose: payment.purpose || '',
    createdBy: payment.creatorName,
    payerName: '',
    payerAccount: '',
    origin: '',
    recipientDetail: '',
    recipientAccount: '',
    recipientName: payment.recipientName,
    currencyCode: 'KGS',
    statusCode: payment.statusCode,
    transferNum: payment.transferNum,
    formattedAmount: formatCurrency(payment.amount),
    paymentStatus: generatePaymentStatusLabel(paramsPayment) || { label: '', classList: '' },
    paymentType: generatePaymentType(Operations.Qr) || '',
    paymentDate: mapDateTime(payment.createdDateTime),
    paymentImg: getPaymentImg(Operations.Qr),
    paymentValueDate: formatDate(Operations.Qr, 'DD.MM.YYYY'),
    selected: false,
    isRepeatable: false,
    isEditable: false,
    isSignable: checkPaymentSignable(payment.statusCode, paramsPayment),
    isSendForSign: checkPaymentSendForSign(payment.statusCode, paramsPayment) || false,
    isExportable: checkPaymentExportable(payment.statusCode, paramsPayment.operationCode),
    isOpenPdf: checkPaymentOpenPdf(payment.statusCode, paramsPayment.operationCode),
    isRevokable: false,
    isRemovable: false,
    isUpdateble: false,
    isRecall: false,
  };
}

// TODO: Нужно поставить тип
export function mapPayment(
  payment:
    | TransferPaymentRecordFragment
    | CardTransferRecordFragment
    | OperationHistoryByCriteriaFragment
    | ConversionPaymentRecordFragment
    | SwiftPaymentRecordFragment
    | BudgetPaymentRecord,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
): any {
  const ruleTrait =
    (payment as OperationHistoryByCriteriaFragment).currency ||
    (payment as TransferPaymentRecordFragment | ConversionPaymentRecordFragment | SwiftPaymentRecordFragment)
      .currencyCode;

  const paramsPayment: ParamsPayment = {
    statusCode: payment.statusCode,
    createdBy: payment.createdBy,
    //TODO: Убрать, когда на бэке добавят поле
    //@ts-ignore
    updatedBy: payment.updatedBy,
    valueDate: payment.valueDate,
    operationCode: payment.operationCode,
    userReferenceId: user.userReferenceId,
    signRight: user.currentContract?.userContractAttributes?.signRight,
    signScheme: user.currentContract?.signScheme,
    signType: user.currentContract?.signType,
    timeRule:
      timeRule.find((item) => item.ruleTrait === ruleTrait) ||
      timeRule.find((item) => item.ruleTrait === RuleTrait.Default)!,
    keyAvailability,
  };

  return {
    ...payment,
    formattedAmount: formatCurrency(payment.amount),
    paymentStatus: generatePaymentStatusLabel(paramsPayment),
    paymentType: generatePaymentType(payment.operationCode),
    paymentDate: mapDateTime(payment.createdDate),
    paymentImg: getPaymentImg(payment.operationCode),
    paymentValueDate: formatDate(payment.valueDate, 'DD.MM.YYYY'),
    selected: false,
    isRepeatable: checkPaymentRepeatable(payment.statusCode, paramsPayment),
    isEditable: checkPaymentEditable(payment.statusCode, paramsPayment),
    isSignable: checkPaymentSignable(payment.statusCode, paramsPayment),
    isExportable: checkPaymentExportable(payment.statusCode, payment.operationCode),
    isOpenPdf: checkPaymentOpenPdf(payment.statusCode, payment.operationCode),
    isRevokable: checkPaymentRevokable(payment.statusCode, paramsPayment),
    isRemovable: checkPaymentRemovable(payment.statusCode, paramsPayment),
    isSendForSign: checkPaymentSendForSign(payment.statusCode, paramsPayment),
    isUpdateble: checkPaymentUpdateble(payment.statusCode, paramsPayment),
    isRecall: checkPaymentRecall(payment.statusCode, paramsPayment),
  };
}

export function mapPaymentNotActions(
  payment:
    | TransferPaymentRecordFragment
    | CardTransferRecordFragment
    | OperationHistoryByCriteriaFragment
    | ConversionPaymentRecordFragment
    | SwiftPaymentRecordFragment
    | BudgetPaymentRecord,
  user: User
): any {
  const paramsPayment: ParamsPaymentSubscription = {
    statusCode: payment.statusCode,
    createdBy: payment.createdBy,
    //TODO: Убрать, когда на бэке добавят поле
    //@ts-ignore
    updatedBy: payment.updatedBy,
    userReferenceId: user.userReferenceId,
    signRight: user.currentContract?.userContractAttributes?.signRight,
  };

  return {
    ...payment,
    formattedAmount: formatCurrency(payment.amount),
    paymentStatus: generatePaymentStatusLabel(paramsPayment),
    paymentType: generatePaymentType(payment.operationCode),
    paymentDate: mapDateTime(payment.createdDate),
    paymentImg: getPaymentImg(payment.operationCode),
    paymentValueDate: formatDate(payment.valueDate, 'DD.MM.YYYY'),
  };
}

const checkPaymentRecall = (status: Status, paramsPayment: ParamsPayment) => {
  const { operationCode, signScheme } = paramsPayment;

  if ([Operations.Conversion, Operations.BudgetPayment, Operations.Refund, Operations.Qr].includes(operationCode))
    return false;

  return (
    ((signScheme === SignScheme.OneSignatory && status === Status.FirstSignInPayControlComplete) ||
      (signScheme === SignScheme.TwoSignatory && status === Status.SecondSignInPayControlComplete)) &&
    +paramsPayment.createdBy === paramsPayment.userReferenceId
  );
};

export function mapInterBankTransferOperations(operations: OperationFragment[]): InterBankTransferOperation[] {
  if (!operations) return [];

  const filteredOperations = operations.filter(
    (operation) =>
      operation.operationCode === OperationTypes.CLEARING || operation.operationCode === OperationTypes.GROSS
  );

  const labels = {
    [OperationTypes.CLEARING]: 'КЛИРИНГ',
    [OperationTypes.GROSS]: 'ГРОСС',
  };

  return filteredOperations.map((operation) => {
    const operationCode = operation.operationCode as keyof typeof labels;
    return {
      ...operation,
      label: labels[operationCode],
      info: '',
      disable: false,
    };
  });
}
export function mapInterBankTransferFormToInput(interBankForm: InterBankFormInput): TransferInput {
  return {
    isInstantPayment: false,
    amount: interBankForm.amount,
    currency: {
      id: Number(interBankForm.currency?.isoNum),
    },
    operation: {
      id: interBankForm.operation!.id,
    },
    payerAccount: interBankForm.payerAccount?.account || '',
    payerBic: {
      id: interBankForm.payerBic!.id,
    },
    payerInn: interBankForm.payerInn,
    payerName: interBankForm.payerName,
    paymentCode: {
      id: interBankForm.paymentCode!.id,
    },
    purpose: interBankForm.purpose,
    recipientAccount: interBankForm.recipientAccount,
    recipientAccountCurrency: {
      id: Number(interBankForm.recipientAccountCurrency?.isoNum),
    },
    recipientBic: {
      id: interBankForm.recipientBic!.id,
    },
    recipientName: interBankForm.recipientName,
    legalPartyId: interBankForm.legalPartyId,
    transferNum: interBankForm.transferNum,
    valueDate: formatDateIso(interBankForm.valueDate),
    departmentId: interBankForm.departmentId as number,
  };
}

export function mapIntraBankTransfer(intraBankTransfer: IntraBankFormInput) {
  return {
    isInstantPayment: false,
    amount: intraBankTransfer.amount,
    purpose: intraBankTransfer.purpose,
    payerInn: intraBankTransfer.payerInn,
    payerName: intraBankTransfer.payerName,
    transferNum: intraBankTransfer.transferNum,
    legalPartyId: intraBankTransfer.legalPartyId,
    recipientName: intraBankTransfer.recipientName,
    recipientAccount: intraBankTransfer.recipientAccount,
    valueDate: formatDateIso(intraBankTransfer.valueDate),
    payerAccount: intraBankTransfer.payerAccount?.account || '',
    currency: {
      id: Number(intraBankTransfer.payerAccount!.currency.isoNum),
    },
    recipientAccountCurrency: {
      id: Number(intraBankTransfer.recipientAccountCurrency!.isoNum),
    },
    payerBic: {
      id: intraBankTransfer.payerBic!.id,
    },
    operation: {
      id: intraBankTransfer.operation!.id,
    },
    paymentCode: intraBankTransfer.paymentCode ? { id: intraBankTransfer.paymentCode.id } : null,
    recipientBic: {
      id: intraBankTransfer.recipientBic!.id,
    },
    departmentId: intraBankTransfer.departmentId as number,
  };
}

export function mapCardTransfer(cardTransfer: CardFormInput): CardTransferInput {
  const rkoOperations = [Operations.BusinessCard_2RkoOwn, Operations.BusinessCard_2RkoForeign];
  const operationCode = cardTransfer.operation?.operationCode as Operations;

  const recipientDetail =
    operationCode === Operations.Rko_2BusinessCardOwn
      ? cardTransfer.recipientCard?.account?.account
      : cardTransfer.recipientDetail;

  const payerAccountId = rkoOperations.includes(operationCode)
    ? cardTransfer.payerCard?.account?.id
    : cardTransfer.payerAccount?.id;

  return {
    isInstantPayment: false,
    amount: cardTransfer.amount,
    purpose: cardTransfer.purpose,
    transferNum: cardTransfer.transferNum,
    legalPartyId: cardTransfer.legalPartyId,
    payerAccountId: payerAccountId || 0,
    recipientDetail: recipientDetail || '',
  };
}

export function mapConversion(conversionInput: ConversionFormInput): ConversionInput {
  return {
    ...conversionInput,
    payerAccount: conversionInput.payerAccount!.account,
    currency: {
      id: Number(conversionInput.payerAccount!.currency.isoNum),
    },
    operation: {
      id: conversionInput.operation!.id,
    },
    payerBic: {
      id: conversionInput.payerBic!.id,
    },
    recipientAccount: conversionInput.recipientAccount!.account,
    recipientAccountCurrency: {
      id: Number(conversionInput.recipientAccount!.currency.isoNum),
    },
    valueDate: formatDateIso(conversionInput.valueDate),
    departmentId: conversionInput.departmentId as number,
  };
}

export function mapSwift(swiftInput: SwiftFormInput): SwiftInput {
  return {
    ...swiftInput,
    kpp: !swiftInput.kpp && swiftInput.currency?.isoCode === Currencies.KZT ? '0000000' : swiftInput.kpp,
    payerAccount: swiftInput.payerAccount!.account,
    currency: {
      id: Number(swiftInput.payerAccount!.currency.isoNum),
    },
    operation: {
      id: swiftInput.operation!.id,
    },
    payerBic: {
      id: swiftInput.payerBic!.id,
    },
    recipientBic: {
      id: swiftInput.recipientBic!.id,
    },
    commissionAccount: swiftInput.commissionAccount!.account,
    commissionCurrency: {
      id: Number(swiftInput.commissionAccount!.currency.isoNum),
    },
    categoryCode: {
      id: swiftInput.categoryCode!.id,
    },
    country: {
      id: swiftInput.country!.id,
    },
    intermediaryBankBic: swiftInput.intermediaryBankBic
      ? {
          id: swiftInput.intermediaryBankBic.id,
        }
      : null,
    exoticCurrency: swiftInput.exoticCurrency?.id
      ? {
          id: swiftInput.exoticCurrency.id,
        }
      : null,
    valueDate: formatDateIso(swiftInput.valueDate),
    departmentId: swiftInput.departmentId as number,
  };
}

export const mapQrPaymentFormToQrTransferInput = (form: QrPaymentForm): QrTransferInput => {
  return {
    isInstantPayment: false,
    invoiceId: null,
    valueDate: null,
    amount: form.amount,
    legalPartyId: form.legalPartyId,
    payerAccountId: form.payerAccount?.accountId || 0,
    purpose: form.purpose,
    qrUrl: form.qrUrl,
  };
};

export function excludeOwnBanks(banks: BankFragment[]): BankFragment[] {
  const ownBanksCode = '109';
  return banks.filter((bank) => !bank.bankCode.startsWith(ownBanksCode));
}

export function excludeStandardCurrencies(currencies: CurrencyForPaymentFragment[]) {
  const standardCurrencies = ['KGS', 'KZT', 'RUB', 'USD', 'GBP', 'EUR', 'CNY', 'JPY'];
  return currencies.filter((currency) => !standardCurrencies.includes(currency.isoCode));
}

export function standardCurrencies(currencies: CurrencyForPaymentFragment[]) {
  const standardCurrencies = ['KGS', 'USD', 'EUR', 'RUB', 'KZT', 'CNY', 'AED', 'TRY', 'UZS', 'GBP'];
  return currencies
    .filter((currency) => standardCurrencies.includes(currency.isoCode))
    .sort((a, b) => standardCurrencies.indexOf(a.isoCode) - standardCurrencies.indexOf(b.isoCode));
}

function checkPaymentRepeatable(status: Status, paramsPayment: ParamsPayment) {
  return (
    ![Operations.Conversion, Operations.BudgetPayment, Operations.Refund, Operations.Qr].includes(
      paramsPayment.operationCode
    ) && ![Status.CanceledSignOne, Status.CanceledSignTwo].includes(status)
  );
}
function checkPaymentEditable(status: Status, paramsPayment: ParamsPayment) {
  if (
    paramsPayment.signType === SignType.PayControl ||
    [Operations.Conversion, Operations.BudgetPayment, Operations.Refund, Operations.Qr].includes(
      paramsPayment.operationCode
    )
  ) {
    return false;
  }

  const firstSignRight =
    status === Status.OnVerification &&
    paramsPayment.signRight === SignRight.First &&
    paramsPayment.signScheme === SignScheme.OneSignatory;
  const secondSignRight = status === Status.PartiallyConfirmed && paramsPayment.signRight === SignRight.Second;
  return firstSignRight || secondSignRight;
}
function checkPaymentRevokable(status: Status, paramsPayment: ParamsPayment) {
  if (paramsPayment.signType === SignType.PayControl) return false;

  const firstSignRight = status === Status.OnVerification && paramsPayment.signRight === SignRight.First;
  const secondSignRight = status === Status.PartiallyConfirmed && paramsPayment.signRight === SignRight.Second;
  return firstSignRight || secondSignRight;
}
function checkPaymentRemovable(status: Status, paramsPayment: ParamsPayment) {
  const includeStatuses = [Status.Draft, Status.ForEditing];
  return includeStatuses.includes(status) && +paramsPayment.createdBy === paramsPayment.userReferenceId;
}
function checkPaymentSendForSign(status: Status, paramsPayment: ParamsPayment) {
  const isValidDate = dayjs().isBefore(dayjs(paramsPayment.valueDate), 'day');
  const isTimeValid = paramsPayment.timeRule;
  const isCurrentValueDate = dayjs().format('YYYY-MM-DD') === paramsPayment.valueDate;
  const isValidDateAnTimeNow = !!isTimeValid?.signIsAllowed && isCurrentValueDate;

  return (
    Status.Draft === status &&
    +paramsPayment.createdBy === paramsPayment.userReferenceId &&
    (isValidDate || isValidDateAnTimeNow) &&
    paramsPayment.keyAvailability?.available
  );
}
function checkPaymentSignable(status: Status, paramsPayment: ParamsPayment) {
  if (paramsPayment.signType === SignType.PayControl) return false;

  const firstSignRight = status === Status.OnVerification && paramsPayment.signRight === SignRight.First;
  const secondSignRight = status === Status.PartiallyConfirmed && paramsPayment.signRight === SignRight.Second;
  return firstSignRight || secondSignRight;
}

function checkPaymentExportable(status: Status, operationCode: Operations) {
  if (operationCode === Operations.Refund) {
    return false;
  }

  return operationCode === Operations.Qr
    ? status === Status.Success
    : ![Status.CanceledSignOne, Status.CanceledSignTwo].includes(status);
}

function checkPaymentOpenPdf(status: Status, operationCode: Operations) {
  if (operationCode === Operations.Refund) return false;

  if (operationCode === Operations.Qr) return status === Status.Success;

  return ![Status.CanceledSignOne, Status.CanceledSignTwo].includes(status);
}

function checkPaymentUpdateble(status: Status, paramsPayment: ParamsPayment) {
  if (
    status === Status.Draft &&
    [
      Operations.Rko_2BusinessCardForeign,
      Operations.Rko_2BusinessCardOwn,
      Operations.Rko_2IndividualCardOwn,
      Operations.Rko_2IndividualCardForeign,
    ].includes(paramsPayment.operationCode)
  ) {
    const isCurrentValueDate = dayjs().format('YYYY-MM-DD') === paramsPayment.valueDate;
    return paramsPayment.timeRule.signIsAllowed && isCurrentValueDate;
  }

  const includesStatuses = [Status.ForEditing, Status.Draft];
  return (
    ![Operations.Conversion, Operations.BudgetPayment, Operations.Refund, Operations.Qr].includes(
      paramsPayment.operationCode
    ) &&
    includesStatuses.includes(status) &&
    +paramsPayment.createdBy === paramsPayment.userReferenceId
  );
}

export function isPaymentConversionType(
  something:
    | OperationHistory
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | null
): something is PaymentConversion {
  return (something as PaymentConversion)?.operationCode === Operations.Conversion;
}

export function isPaymentTransferType(
  something:
    | PaymentTransfer
    | PaymentTransferByInvoice
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrTransfer
    | null
): something is PaymentTransfer {
  const transfer = [
    Operations.IntrabankTransferOwn,
    Operations.IntrabankTransferForeign,
    Operations.Gross,
    Operations.Clearing,
  ];
  return transfer.includes((something as PaymentTransfer).operationCode);
}

export function isTransferForm(form: PaymentForm | ConversionFormInput): form is TransferForm {
  return [
    Operations.Clearing,
    Operations.Gross,
    Operations.IntrabankTransferOwn,
    Operations.IntrabankTransferForeign,
  ].includes(form.operation?.operationCode as Operations);
}

export function isPaymentType(
  something:
    | PaymentTransfer
    | PaymentTransferByInvoice
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | null
): something is
  | PaymentTransfer
  | PaymentTransferByInvoice
  | PaymentConversion
  | PaymentSwift
  | PaymentCardTransfer
  | PaymentBudget
  | PaymentQrReturned
  | PaymentQrTransfer {
  return (
    !!(something as PaymentTransfer | PaymentConversion | PaymentSwift | PaymentBudget)?.id &&
    !!(something as PaymentTransfer | PaymentConversion | PaymentSwift | PaymentBudget)?.operationCode
  );
}

export function isPaymentTransferByInvoiceType(
  something:
    | PaymentTransfer
    | PaymentTransferByInvoice
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrTransfer
    | null
): something is PaymentTransferByInvoice {
  if (!something) return false;

  return something.operationCode === Operations.TransferByInvoice && 'invoiceId' in something;
}

export function isPaymentSwiftType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | null
): something is PaymentSwift {
  if (!something) return false;

  return something.operationCode === Operations.Swift;
}

export function isPaymentRko2CardTransferType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | null
): something is PaymentCardTransfer {
  if (!something) return false;

  return [
    Operations.Rko_2BusinessCardForeign,
    Operations.Rko_2BusinessCardOwn,
    Operations.Rko_2IndividualCardOwn,
    Operations.Rko_2IndividualCardForeign,
    Operations.BusinessCard_2RkoOwn,
    Operations.BusinessCard_2RkoForeign,
  ].includes(something.operationCode);
}

export function isPaymentRko2BusinessCardType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | null
): something is PaymentCardTransfer {
  if (!something) return false;

  return [Operations.Rko_2BusinessCardOwn].includes(something.operationCode);
}

export function isPaymentRko2IndividualCardType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | null
): something is PaymentCardTransfer {
  if (!something) return false;

  return [
    Operations.Rko_2IndividualCardOwn,
    Operations.Rko_2IndividualCardForeign,
    Operations.Rko_2BusinessCardForeign,
  ].includes(something.operationCode);
}

export function isPaymentBusinessCardRko2Type(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentRko2CardTransfer
    | OperationHistory
    | PaymentQrReturned
    | PaymentQrTransfer
    | PaymentBudget
    | null
): something is PaymentRko2CardTransfer {
  if (!something) return false;

  return [Operations.BusinessCard_2RkoOwn, Operations.BusinessCard_2RkoForeign].includes(something.operationCode);
}

export function isCardFormInput(something: PaymentForm | null): something is CardFormInput {
  if (!something) return false;

  return !('recipientAccount' in something);
}

export function isOperationHistoryType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | OperationHistory
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | null
): something is OperationHistory {
  if (!something) return false;
  return (something as OperationHistory).currency !== undefined;
}

export function isPaymentBudgetType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentBudget
    | PaymentCardTransfer
    | PaymentQrReturned
    | PaymentQrTransfer
    | OperationHistoryByCriteriaFragment
    | null
): something is PaymentBudget {
  if (!something) return false;

  return (something as PaymentBudget).operationCode === Operations.BudgetPayment;
}

export function isPaymentQrReturnedType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | OperationHistoryByCriteriaFragment
    | null
): something is PaymentQrReturned {
  if (!something) return false;

  return something.operationCode === Operations.Refund;
}

export function isPaymentQrTransferType(
  something:
    | PaymentTransfer
    | PaymentConversion
    | PaymentSwift
    | PaymentCardTransfer
    | PaymentBudget
    | PaymentQrReturned
    | PaymentQrTransfer
    | OperationHistoryByCriteriaFragment
    | null
): something is PaymentQrTransfer {
  if (!something) return false;

  return something.operationCode === Operations.Qr;
}

export function mapPaymentByCriteria(payload: PaymentByCriteriaParams) {
  if (!payload.payments.list.length) {
    return {
      amountPages: 0,
      amountElements: 0,
      currentPage: 1,
      list: [],
    };
  }
  return {
    ...payload.payments,
    list: mapPaymentHistory(payload.payments.list, payload.user),
  };
}

export function formattedModelProcess(image: string | null): string | undefined {
  if (!image) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(image, 'image/svg+xml');

  const base64 = new XMLSerializer().serializeToString(doc.documentElement);
  return `data:image/svg+xml;base64,${window.btoa(base64)}`;
}

export function mapOperationTypeCategoryByCodeCriteria(classifications: PaginatedOperationTypeCategory | null) {
  if (!classifications) return [];
  return classifications.list.filter((item) => item.isActive);
}

export function mapBudgetClassificationByCodeCriteria(categories: PaginatedBudgetClassificationCode | null) {
  if (!categories) return [];
  return categories.list.filter((item) => item.isActive);
}

export function mapBanksByCode(banks: PaginatedBank | null) {
  if (!banks) return [];
  return excludeOwnBanks(banks.list.filter((item) => item.isActive));
}

export function mapIntraBankOperation(operations: OperationFragment[]) {
  const foreign = operations.find((operation) => operation.operationCode === OperationTypes.INTRABANK_TRANSFER_FOREIGN);
  const own = operations.find((operation) => operation.operationCode === OperationTypes.INTRABANK_TRANSFER_OWN);

  if (!foreign || !own) return null;

  return {
    foreign,
    own,
  };
}

export function findSwiftOperation(operations: OperationFragment[]) {
  return operations.find((operation) => operation.operationCode === OperationTypes.SWIFT) || null;
}
export function findConversionOperation(operations: OperationFragment[]) {
  return operations.find((operation) => operation.operationCode === OperationTypes.CONVERSION) || null;
}
export function findBudgetPaymentOperation(operations: OperationFragment[]) {
  return operations.find((operation) => operation.operationCode === OperationTypes.BUDGET_PAYMENT) || null;
}
export function excludeRestrictedCountries(countries: CountryFragment[]) {
  //Иран, Куба, Корея(КНДР), Сирия, Мьянма
  const restricted = new Set(['9364', '9192', '9408', '9760', '9104']);
  return countries.filter(({ numInList }) => !restricted.has(numInList));
}

export function mapRko2CardOperations(operations: OperationFragment[]) {
  const business = operations.filter((operation) =>
    [OperationTypes.RKO_2_BUSINESS_CARD_OWN].includes(operation.operationCode as OperationTypes)
  );
  const individual = operations.filter((operation) =>
    [
      OperationTypes.RKO_2_INDIVIDUAL_CARD_OWN,
      OperationTypes.RKO_2_INDIVIDUAL_CARD_FOREIGN,
      OperationTypes.RKO_2_BUSINESS_CARD_FOREIGN,
    ].includes(operation.operationCode as OperationTypes)
  );
  const rko = operations.filter((operation) =>
    [OperationTypes.BUSINESS_CARD_2_RKO_OWN, OperationTypes.BUSINESS_CARD_2_RKO_FOREIGN].includes(
      operation.operationCode as OperationTypes
    )
  );

  if (!business || !individual || !rko) return null;

  return {
    business,
    individual,
    rko,
  };
}

export function isSearchOperationHistoryInputType(
  something: SearchOperationHistoryInput | {}
): something is SearchOperationHistoryInput {
  return !!Object.keys(something as SearchOperationHistoryInput).length;
}

export function mapSignatories(
  signatories: PaymentStatusHistoryFragment[],
  signScheme: SignScheme,
  statusCode?: Status
): PaymentStatusHistoryInfo[] {
  const data = [
    {
      updatedBy: 'SIGNATORIES.PRIMARY_SIGNATURE',
      label: 'SIGNATORIES.PROCESSING_SIGNATURE',
    },
    {
      updatedBy: 'SIGNATORIES.SECONDARY_SIGNATURE',
      label: 'SIGNATORIES.PROCESSING_SIGNATURE',
    },
  ];
  const formattedSignatories = signatories.map((signatory) => {
    const label = `SIGNATORIES.${signatory.statusCode}`;
    return {
      ...signatory,
      label,
      isSigned: true,
    };
  });
  const excludeStatus = [Status.CanceledSignTwo, Status.ForEditing, Status.Success];

  if (!formattedSignatories.length) return signScheme === SignScheme.OneSignatory ? [data[0]] : data;

  if (
    formattedSignatories.length === 1 &&
    signScheme === SignScheme.TwoSignatory &&
    !excludeStatus.includes(formattedSignatories[0].statusCode)
  ) {
    return [...formattedSignatories, data[0]];
  }

  if (statusCode && statusCode !== Status.ForEditing) {
    const result = formattedSignatories.filter((item) => item.statusCode !== Status.ForEditing);
    if (!result.length) return signScheme === SignScheme.OneSignatory ? [data[0]] : data;
    return formattedSignatories.filter((item) => item.statusCode !== Status.ForEditing);
  }

  return formattedSignatories;
}

export interface StatusHistoryMap {
  label: string;
  iconClassList: string;
  labelClassList: string;
  time?: string;
  date?: string;
}
export function mapPaymentStatusHistoryByPaymentId(history: PaymentStatusHistoryFragment[], statusCode: Status) {
  if (!history || history.length === 0) return;

  const lastStatus = history[history.length - 1].statusCode;
  const authUserStore = useAuthUserStore();
  const signScheme = authUserStore?.selectedUserContract?.signScheme;
  const isSignScheme = signScheme === SignScheme.OneSignatory;
  const statusHistory: StatusHistoryMap[] = history.map((item) => {
    let labelClassList: string;
    let iconClassList: string;

    switch (item.statusCode) {
      case Status.Success:
        labelClassList = 'status-success';
        iconClassList = 'status-success';
        break;
      case Status.Error:
      case Status.CanceledSignTwo:
      case Status.CanceledSignOne:
      case Status.Revoke:
      case Status.TimeoutCanceledSignOne:
      case Status.TimeoutCanceledSignTwo:
      case Status.TimeIsOut:
        labelClassList = 'status-danger';
        iconClassList = 'status-danger';
        break;
      case Status.WaitResponseFromBank:
        labelClassList = lastStatus === Status.WaitResponseFromBank ? 'status-warning' : '';
        iconClassList = lastStatus === Status.WaitResponseFromBank ? 'status-warning' : 'status-success';
        break;
      case Status.Kvit:
        labelClassList = lastStatus === Status.Kvit ? 'status-warning' : '';
        iconClassList = lastStatus === Status.Kvit ? 'status-warning' : 'status-success';
        break;
      default:
        labelClassList = '';
        iconClassList = 'status-success';
        break;
    }
    const status =
      !isSignScheme && item.statusCode === Status.OnVerificationCompleted
        ? Status.OnVerificationCompleted + '_SUFFIX'
        : item.statusCode;
    return {
      date: formatDate(item.updatedDate, 'DD.MM.YYYY'),
      time: formatDate(item.updatedDate, 'HH:mm:ss'),
      label: `HISTORY.STATUS_HISTORY.${status}`,
      iconClassList,
      labelClassList,
    };
  });
  const completedStatuses = [
    Status.CanceledSignOne,
    Status.CanceledSignTwo,
    Status.Success,
    Status.Error,
    Status.Revoke,
    Status.TimeoutCanceledSignOne,
    Status.TimeoutCanceledSignTwo,
    Status.TimeIsOut,
  ];
  const isCompleted = completedStatuses.includes(lastStatus);
  if (!isCompleted) {
    switch (statusCode) {
      case Status.Draft:
      case Status.ForEditing:
        if (!isSignScheme) {
          statusHistory.push({
            label: `HISTORY.STATUS_HISTORY.${Status.PartialConfirmationCompleted}`,
            iconClassList: 'status-muted',
            labelClassList: 'status-muted',
          });
        }
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${
            isSignScheme ? Status.OnVerificationCompleted : Status.OnVerificationCompleted + '_SUFFIX'
          }`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.WaitResponseFromBank}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Kvit}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Success}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        break;
      case Status.SecondSignInPayControl:
      case Status.SecondSignInPayControlComplete:
      case Status.PartiallyConfirmed:
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.PartialConfirmationCompleted}`,
          iconClassList: 'status-warning',
          labelClassList: 'ob-text-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.OnVerificationCompleted}_SUFFIX`,
          iconClassList: 'ob-text-muted',
          labelClassList: 'ob-text-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.WaitResponseFromBank}`,
          iconClassList: 'ob-text-muted',
          labelClassList: 'ob-text-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Kvit}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Success}`,
          iconClassList: 'ob-text-muted',
          labelClassList: 'ob-text-muted',
        });
        break;
      case Status.FirstSignInPayControl:
      case Status.FirstSignInPayControlComplete:
      case Status.OnVerification:
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${
            isSignScheme ? Status.OnVerificationCompleted : Status.OnVerificationCompleted + '_SUFFIX'
          }`,
          iconClassList: 'status-warning',
          labelClassList: 'ob-text-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.WaitResponseFromBank}`,
          iconClassList: 'ob-text-muted',
          labelClassList: 'ob-text-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Kvit}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Success}`,
          iconClassList: 'ob-text-muted',
          labelClassList: 'ob-text-muted',
        });
        break;
      case Status.WaitResponseFromBank:
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Kvit}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Success}`,
          iconClassList: 'status-muted',
          labelClassList: 'ob-text-muted',
        });

        break;
      default:
        statusHistory.push({
          label: `HISTORY.STATUS_HISTORY.${Status.Success}`,
          iconClassList: 'status-muted',
          labelClassList: 'status-muted',
        });
        break;
    }
  }

  return statusHistory;
}

export function mapTransferTemplate(form: PaymentForm, nameTemplate: string): TransferTemplateInput {
  return {
    legalPartyId: form.legalPartyId,
    operation: {
      id: form.operation!.id,
    },
    nameTemplate,
    payerAccount: form.payerAccount!.account,
    payerBic: !isCardFormInput(form) ? form.payerBic!.bankCode : null,
    amount: form.amount,
    recipientInn: '',
    purpose: form.purpose,
    paymentCode: (form as TransferForm)?.paymentCode?.classCode ?? null,
    categoryCode: (form as SwiftFormInput)?.categoryCode?.categoryCode ?? null,
    commissionAccount: (form as SwiftFormInput)?.commissionAccount?.account ?? null,
    commissionCurrency: (form as SwiftFormInput)?.commissionCurrency?.isoCode ?? null,
    commissionOption: (form as SwiftFormInput)?.commissionOption ?? null,
    corrAccount: (form as SwiftFormInput)?.corrAccount ?? null,
    country: (form as SwiftFormInput)?.country?.fullName ?? null,
    rubCodeVo: (form as SwiftFormInput)?.rubCodeVo ?? null,
    rubInn: (form as SwiftFormInput)?.rubInn ?? null,
    kpp: (form as SwiftFormInput)?.kpp ?? null,
    city: (form as SwiftFormInput)?.city ?? null,
    address: (form as SwiftFormInput)?.address ?? null,
    kztBin: (form as SwiftFormInput)?.kztBin ?? null,
    intermediaryBankBic: (form as SwiftFormInput)?.intermediaryBankBic?.bankCode ?? null,
    intermediaryCorrAccount: (form as SwiftFormInput)?.intermediaryCorrAccount ?? null,
    exoticAmount: (form as SwiftFormInput)?.exoticAmount ?? null,
    exoticRate: (form as SwiftFormInput)?.exoticRate ?? null,
    exoticCurrency: (form as SwiftFormInput)?.exoticCurrency?.isoCode ?? null,
    isExotic: (form as SwiftFormInput)?.isExotic ?? null,
    recipientCardAccount: (form as CardFormInput)?.recipientCard?.account.account ?? null,
    recipientCardNum: (form as CardFormInput)?.recipientCard?.cardNum ?? null,
    payerCurrency: !isCardFormInput(form) ? form.currency!.isoCode : '',
    recipientBic: !isCardFormInput(form) ? form.recipientBic!.bankCode : '',
    recipientAccount: !isCardFormInput(form) ? form.recipientAccount : '',
    recipientName: !isCardFormInput(form) ? form.recipientName : '',
    recipientCardType: !isCardFormInput(form) ? null : 'Visa Business',
    recipientDetail: !isCardFormInput(form) ? '' : form.recipientDetail,
    payerCardNum: null,
    payerCardType: null,
    payerCardAccount: null,
  };
}

export function mapEditTransferTemplate(form: PaymentForm, id: number, nameTemplate: string): TemplateInput {
  return {
    id,
    ...mapTransferTemplate(form, nameTemplate),
  };
}

export function formatListenPayment(payment: ListenPayment, user: User): ListenPaymentHistory {
  const historyPayment = mapPaymentNotActions(payment.data.listenPayment.paymentInformation, user);
  return {
    paymentInformation: historyPayment,
    state: payment.data.listenPayment.state,
  };
}

export function formattedCurrencyRates(currencyRates: CurrencyRatesFragment) {
  return {
    cash: mapCurrencyRates(currencyRates.cash),
    cashless: mapCurrencyRates(currencyRates.cashless),
    card: mapCurrencyRates(currencyRates.card),
    nbkr: mapCurrencyRates(currencyRates.nbkr),
  };
}

function mapCurrencyRates(rates: CurrencyRateFragment[]) {
  return rates.filter((rate) => rate.purchaseRate || rate.salesRate);
}

export function replaceSpecialCharsForPayment(input: string): string {
  const replacements: { [key: string]: string } = {
    '[^[:print:]]': ' ',
    '%': 'PRC',
    '№': 'NO',
    '@': '(at)',
    '\\$': 'USD',
    '&': 'AND',
    '–': '-',
  };
  let result = input.replace(/«/g, '"').replace(/»/g, '"');
  for (const [key, value] of Object.entries(replacements)) {
    const regex = new RegExp(`^${key}|${key}`, 'g');
    result = result.replace(regex, () => {
      return key.length === input.length ? `${value} ` : ` ${value} `;
    });
  }
  result = result.replace(/\s{2,}/g, ' ');
  return result;
}

export function mapFilterQrTransaction<
  T extends QrOperationFilterInput | PersonalQrOperationFilterInput | QrTransferFilterInput,
>(filter: T): T {
  return {
    ...filter,
    endTime: filter.endTime?.length ? `${filter.endTime}:59` : null,
    startTime: filter.startTime?.length ? `${filter.startTime}:00` : null,
    startDate: filter.startDate?.length ? formatDateIso(filter.startDate) : null,
    endDate: filter.endDate?.length ? formatDateIso(filter.endDate) : null,
    startAmount: filter.startAmount ? filter.startAmount : null,
    endAmount: filter.endAmount ? filter.endAmount : null,
  };
}

function checkSalaryRevokable(status: Status, signRight: SignRight, signType: SignType) {
  if (signType === SignType.PayControl) return false;

  const firstSignRight = status === Status.OnVerification && signRight === SignRight.First;
  const secondSignRight = status === Status.PartiallyConfirmed && signRight === SignRight.Second;
  return firstSignRight || secondSignRight;
}

function checkSalarySignable(status: Status, signRight: SignRight, signType: SignType) {
  if (signType === SignType.PayControl) return false;

  const firstSignRight = status === Status.OnVerification && signRight === SignRight.First;
  const secondSignRight = status === Status.PartiallyConfirmed && signRight === SignRight.Second;
  return firstSignRight || secondSignRight;
}

export function mapSalary(
  salary: SalaryRecordFragment,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): SalaryStatement {
  const salaryResult = {
    ...salary,
    statusLabel: generateSalaryStatementStatusLabel(salary.status, signRight, salary.isFullComplete),
    createdDate: formatDate(salary.createdDate, 'DD.MM.YYYY HH:mm'),
    isSendForSign: salary.status === Status.Draft && signRight !== SignRight.Auditor && keyAvailability.available,
    isRemovable: salary.status === Status.Draft && signRight !== SignRight.Auditor,
    isRevokable: checkSalaryRevokable(salary.status, signRight, signType),
    isSignable: checkSalarySignable(salary.status, signRight, signType),
    isOpen: true,
    isDownloadPdf: salary.status === Status.Success,
    isRepeatStatement: salary.status === Status.Success || salary.status === Status.Error,
    status: salary.status,
  };
  return {
    ...salaryResult,
    hasAction:
      salaryResult.isSendForSign ||
      salaryResult.isRemovable ||
      salaryResult.isRevokable ||
      salaryResult.isSignable ||
      salaryResult.isDownloadPdf ||
      salaryResult.isRepeatStatement,
  };
}

export function mapSalaries(
  files: PaginatedSalaryFragment,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): PaginatedSalaryStatement {
  return {
    ...files,
    list: files.list.map((item) => mapSalary(item, signRight, signType, keyAvailability)),
  };
}

export function mapSalaryDetail(
  details: SalaryInfoWithDetailFragment,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): SalaryInfoWithDetail {
  return {
    ...details,
    createdDate: formatDate(details.createdDate, 'DD.MM.YYYY HH:mm'),
    statusLabel: generateSalaryStatementStatusLabel(
      details.status,
      signRight,
      details.salaryDetailResponse.list[0].state
    ),
    isSendForSign: details.status === Status.Draft && signRight !== SignRight.Auditor && keyAvailability.available,
    isRemovable: details.status === Status.Draft && signRight !== SignRight.Auditor,
    isRevokable: checkSalaryRevokable(details.status, signRight, signType),
    isSignable: checkSalarySignable(details.status, signRight, signType),
    isDownloadPdf: details.status === Status.Success,
    isRepeatStatement: details.status === Status.Success || details.status === Status.Error,
    status: details.status,
  };
}

export function isSalaryStatementPaymentService(
  something: SalaryStatement | SalaryContent | null
): something is SalaryStatement {
  if (!something) return false;

  return !!(
    (something as SalaryStatement).origin &&
    (something as SalaryStatement).paymentId &&
    (something as SalaryContent).id
  );
}

export function mapFilterQrOperationInstallment(filter: InstallmentFilter) {
  const { toSum, fromSum, toDate, fromDate, statuses } = filter.filterDto;
  return {
    endAmount: toSum !== 0 ? toSum : 10000000,
    endDate: toDate ? formatDateIso(toDate) : date.formatDate(Date.now(), 'YYYY-MM-DD'),
    legalPartyId: filter.legalPartyId,
    qrOperationType: [QrOperationType.Payda, QrOperationType.PaydaPos, QrOperationType.PaydaCollab],
    salePointCode: filter.locationCode,
    startAmount: fromSum ?? 0,
    startDate: fromDate ? formatDateIso(fromDate) : date.formatDate(Date.now(), 'YYYY-MM-DD'),
    qrOperationStatus: statuses as QrOperationStatus[],
  } as QrOperationInstallmentFilterInput;
}

const mapPayDaInstallmentOperation = (data: QrOperationInfoInstallmentFragment): InstallmentOperation => {
  return {
    clientName: data.senderName ?? '',
    sum: data.amountWithFee,
    createdDate: formatDateView(data.createdDate),
    salePointName: data.salePointName ?? '',
    claimNumber: data.stan,
    status: (data.qrOperationStatus as QrOperationStatus) ?? QrOperationStatus.InProcess,
    product: {
      name: 'Pay,Da!',
      color: 'negative',
    },
    duration: null,
    loanClaimId: '',
    salePointCode: null,
    note: null,
    percentRate: '',
  };
};

// TODO: Поставить тип
export function mapPayDaInstallmentOperations(data: any): InstallmentOperations {
  return {
    totalPages: data.amountPages,
    operations: data.list.map((item: QrOperationInfoInstallmentFragment) => mapPayDaInstallmentOperation(item)),
  };
}

export const tittlePaymentCode: Record<string, string> = {
  437: 'E_PATENT',
  492: 'GUARANTEE_FEE_ETP_FUGI',
  1292: 'AIS_FOREST_USE',
  1526: 'GUARANTEE_FEE_ETP_MSU',
  1527: 'SOCIAL_INSURANCE_PAYMENT',
  2032: 'INSURANCE_CONTRIBUTION_PAYMENT_BY_CODE',
  709: 'ELECTRONIC_NOTARY',
  1525: 'BUDGET_CREDIT_REPAYMENT',
  2588: 'JUDICIAL_DEPARTMENT_GUARANTEE_FEE',
  2589: 'JUDICIAL_DEPARTMENT_PROPERTY_PAYMENT',
  1669: 'JUDICIAL_DEPARTMENT_ENFORCEMENT_FEE_10_PERCENT',
};
